import { monthLabels } from "@/constants/months";
import { normalizeDecimal } from "@/utils/normalizeDecimal";
import type {
  AddCostFormValues,
  FormulaVariable,
  PlanningMonth,
} from "../components/CostPanel/CostPanel.types";
import type { CalculationCategoryResult } from "./useCalculationDetail";

type SaveInputEntry =
  | {
      variableId: string;
      dimensionValue: string | null;
      distributionType: "PER_MONTH";
      monthValues: { year: number; monthNumber: number; value: number }[];
    }
  | {
      variableId: string;
      dimensionValue: string | null;
      distributionType: "MONTHLY" | "YEARLY";
      singleValue: number | null;
    };

export const buildSaveInputs = (
  formValues: AddCostFormValues,
  formulaVariables: FormulaVariable[],
  planningMonths: PlanningMonth[],
  activeDimensionValue: string | null,
): SaveInputEntry[] =>
  formulaVariables.map<SaveInputEntry>((variable) => {
    if (variable.inputType === "PER_MONTH") {
      const monthMap = formValues[variable.id] as
        | Record<string, string>
        | undefined;
      return {
        variableId: variable.id,
        dimensionValue: activeDimensionValue,
        distributionType: "PER_MONTH",
        monthValues: planningMonths.flatMap((month) => {
          const raw = monthMap?.[month.key];
          if (raw === undefined || raw === "") return [];
          const [year, monthNumber] = month.key.split("-").map(Number);
          return [
            { year, monthNumber, value: parseFloat(normalizeDecimal(raw)) },
          ];
        }),
      };
    }
    const raw = formValues[variable.id] as string | undefined;
    return {
      variableId: variable.id,
      dimensionValue: activeDimensionValue,
      distributionType: variable.inputType as "MONTHLY" | "YEARLY",
      singleValue:
        raw === undefined || raw === ""
          ? null
          : parseFloat(normalizeDecimal(raw)),
    };
  });

export const pickActiveBucket = (
  category: CalculationCategoryResult,
  activeDimensionValue: string | null,
) =>
  category.formulas.find((b) => b.dimensionValue === activeDimensionValue) ??
  category.formulas[0];

export const derivePlanningMonths = (
  startDate: string,
  endDate: string,
): PlanningMonth[] => {
  const parseUTC = (iso: string) => {
    const [y, m, d] = iso.split("T")[0].split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };
  const start = parseUTC(startDate);
  const end = parseUTC(endDate);
  const months: PlanningMonth[] = [];
  const currentMs = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1);
  const endMs = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1);
  let current = new Date(currentMs);
  const endMonth = new Date(endMs);

  while (current <= endMonth) {
    const monthIndex = current.getUTCMonth();
    months.push({
      key: `${current.getUTCFullYear()}-${String(monthIndex + 1).padStart(2, "0")}`,
      label: monthLabels[monthIndex],
    });
    current = new Date(Date.UTC(current.getUTCFullYear(), monthIndex + 1, 1));
  }

  return months;
};
